import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminCrmService from '../../../services/adminCrmService';
import propertyService from '../../../services/propertyService';
import '../../../assets/styles/PersonalIDs.css';
import '../../assets/styles/CRMOpenLead.css';
import arrowleftt from '../../assets/img/arrowleftt.svg';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  active:   { bg: '#D1FAE5', text: '#059669' },
  pending:  { bg: '#FEF3C7', text: '#D97706' },
  approved: { bg: '#D1FAE5', text: '#059669' },
  rejected: { bg: '#FEE2E2', text: '#DC2626' },
  deactive: { bg: '#F3F4F6', text: '#6B7280' },
  inactive: { bg: '#F3F4F6', text: '#6B7280' },
};

const TABS = [
  { key: 'all',      label: 'All'      },
  { key: 'active',   label: 'Active'   },
  { key: 'pending',  label: 'Pending'  },
  { key: 'rejected', label: 'Rejected' },
  { key: 'deactive', label: 'Deactive' },
];

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://sandbox.rebax.in';

// Prefers the project thumbnail; falls back to the first gallery image.
const getFirstImage = (images, thumbnail) => {
  try {
    const arr = typeof images === 'string' ? JSON.parse(images) : images;
    const img = thumbnail || (Array.isArray(arr) && arr.length > 0 ? arr[0] : null);
    if (!img) return null;
    return img.startsWith('http') ? img : `${API_BASE}${img}`;
  } catch { return null; }
};

const formatCurrency = (min, max) => {
  const fmt = (v) => {
    if (!v) return null;
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)} Cr`;
    if (v >= 100000)   return `${(v / 100000).toFixed(1)} L`;
    return `${(v / 1000).toFixed(0)}K`;
  };
  const a = fmt(min), b = fmt(max);
  if (a && b) return `${a} - ${b}`;
  return a || b || 'N/A';
};

export default function AdminCrmManageProjects() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { userId, userName, userRole, userEmail, userPhone } = location.state || {};

  // List state
  const [loading,     setLoading]     = useState(true);
  const [allProjects, setAllProjects] = useState([]);
  const [activeTab,   setActiveTab]   = useState('all');
  const [search,      setSearch]      = useState('');

  // Action menu
  const [menuVisible,     setMenuVisible]     = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [actionLoading,   setActionLoading]   = useState(false);

  // Delete modal
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  // Approve/Task modal
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalLoading,      setApprovalLoading]      = useState(false);

  // The backend can only filter this endpoint by the `status` column
  // (active/inactive) - it has no way to select on `approval_status`
  // (pending/approved/rejected). So every project is fetched once and the
  // Active/Pending/Rejected/Deactive tabs are matched client-side, the same
  // way the builder's own Manage Projects screen does it.
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      let items = [];
      let page = 1;
      const limit = 100;
      while (true) {
        const res = await adminCrmService.getUserProjects(userId, { page, limit });
        if (!res.success) break;
        items = items.concat(res.items || []);
        if ((res.items || []).length < limit || items.length >= (res.total || 0)) break;
        page++;
      }
      setAllProjects(items);
      loadThumbnails(items);
    } catch (err) {
      console.error('Fetch projects error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // The list endpoint doesn't return each project's thumbnail, so fetch it
  // from the project's own record and merge it in. Cards render straight away
  // with the first gallery image and swap once the thumbnail arrives.
  const loadThumbnails = (items) => {
    items.forEach(async (item) => {
      if (item.thumbnail) return;
      try {
        const res = await propertyService.getPropertyById(item.id);
        const data = Array.isArray(res?.data) ? res.data[0] : res?.data;
        if (data?.thumbnail) {
          setAllProjects(prev => prev.map(p => (p.id === item.id ? { ...p, thumbnail: data.thumbnail } : p)));
        }
      } catch (err) {
        console.error('Error loading thumbnail for project', item.id, err);
      }
    });
  };

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const matchesTab = (project, tab) => {
    const approval = project.approval_status || 'approved';
    if (tab === 'all') return true;
    if (tab === 'active') return project.status === 'active' && approval === 'approved';
    if (tab === 'deactive') return project.status !== 'active' && approval !== 'rejected' && approval !== 'pending';
    if (tab === 'pending') return approval === 'pending';
    if (tab === 'rejected') return approval === 'rejected';
    return true;
  };

  const matchesSearch = (p) => !search.trim() || (p.property_title || '').toLowerCase().includes(search.trim().toLowerCase());

  const projects = allProjects.filter(p => matchesSearch(p) && matchesTab(p, activeTab));
  const total = projects.length;

  const getTabCount = (tab) => allProjects.filter(p => matchesSearch(p) && matchesTab(p, tab)).length;

  // ── Action handlers ──────────────────────────────────────────

  const openActionMenu = (project) => {
    setSelectedProject(project);
    setMenuVisible(true);
  };

  // Open project details (matching mobile app openProjectDetail)
  const openProjectDetail = (project) => {
    navigate(`/admin-projectdetails/${project.id}`, { state: { project, builderName: userName } });
  };

  const handleEditProject = (project) => {
    setMenuVisible(false);
    navigate('/AdminCrmAddProject', {
      state: { userId, userName, userRole, userEmail, userPhone, project: project || selectedProject, mode: 'edit' }
    });
  };

  const handleDeleteProject = () => {
    setMenuVisible(false);
    setDeleteModalVisible(true);
  };

  const confirmDeleteProject = async () => {
    if (!selectedProject) return;
    setActionLoading(true);
    try {
      const res = await adminCrmService.deleteProjectForBuilder(userId, selectedProject.id);
      if (res?.success || res?.data?.success || res?.data?.deleted) {
        setDeleteModalVisible(false);
        setSelectedProject(null);
        toast.success('Project has been deleted successfully.');
        fetchProjects();
      } else {
        toast.error(res?.message || 'Failed to delete project');
      }
    } catch (err) {
      console.error('Delete project error:', err);
      toast.error(err?.response?.data?.message || 'Failed to delete project. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveFromMenu = () => {
    setMenuVisible(false);
    setApprovalModalVisible(true);
  };

  const handleApproveNow = async () => {
    if (!selectedProject) return;
    setApprovalLoading(true);
    try {
      const res = await adminCrmService.approveProjectFromCrm(userId, selectedProject.id);
      setApprovalLoading(false);
      setApprovalModalVisible(false);
      if (res?.success || res?.data) {
        toast.success('Project approved successfully!');
        fetchProjects();
      } else {
        toast.error(res?.message || 'Failed to approve project');
      }
    } catch (err) {
      setApprovalLoading(false);
      toast.error(err?.response?.data?.message || 'Failed to approve project');
    }
  };

  const handleSendToTask = async () => {
    if (!selectedProject) return;
    setApprovalLoading(true);
    try {
      const res = await adminCrmService.sendProjectToTask(userId, selectedProject.id);
      setApprovalLoading(false);
      setApprovalModalVisible(false);
      if (res?.success || res?.data) {
        toast.success('Project sent to verification task.');
        fetchProjects();
      } else {
        toast.error(res?.message || 'Failed to send to task');
      }
    } catch (err) {
      setApprovalLoading(false);
      toast.error(err?.response?.data?.message || 'Failed to send to task');
    }
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <div className="d-flex" style={{ background: '#F9FAFB', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-3" style={{ paddingBottom: '100px' }}>

          {/* Back + Title */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <h6 className="accordion-title mb-0">Manage Projects</h6>
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#2563EB', backgroundColor: '#EFF6FF', padding: '4px 12px', borderRadius: '10px' }}>
              {total}
            </span>
          </div>

          {/* Search */}
          <div className="d-flex gap-2 mb-3">
            <input
              type="text"
              className="form-control personal-input"
              placeholder="Search projects..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
            />
          </div>

          {/* Tabs */}
          <div className="d-flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '6px 14px', borderRadius: '16px', border: 'none',
                  backgroundColor: activeTab === tab.key ? '#2563EB' : '#F3F4F6',
                  color: activeTab === tab.key ? '#fff' : '#6B7280',
                  fontSize: '12px', fontWeight: '500', cursor: 'pointer'
                }}
              >{tab.label} ({getTabCount(tab.key)})</button>
            ))}
          </div>

          {userName && <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '12px' }}>{userName}</p>}

          {/* Project grid */}
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><div className="mt-3" style={{ color: '#888' }}>Loading...</div></div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5">
              <div style={{ fontSize: '48px' }}>🏠</div>
              <div style={{ fontSize: '17px', fontWeight: '600', color: '#374151', marginTop: '12px' }}>No Projects</div>
              <div style={{ fontSize: '14px', color: '#9CA3AF', marginTop: '6px' }}>No projects found for this builder</div>
            </div>
          ) : (
            <>
              <div className="row g-3">
                {projects.map((item, index) => {
                  const statusC   = STATUS_COLORS[item.status]          || STATUS_COLORS.inactive;
                  const approvalC = STATUS_COLORS[item.approval_status] || STATUS_COLORS.pending;
                  const imageUrl  = getFirstImage(item.images, item.thumbnail);
                  return (
                    <div key={item.id || index} className="col-12 col-md-6 col-xl-4">
                      <div
                        onClick={() => openProjectDetail(item)}
                        style={{
                          backgroundColor: '#fff', borderRadius: '14px', overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #F3F4F6',
                          height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer'
                        }}>

                        {/* Image + action icons overlay */}
                        <div style={{ position: 'relative', height: '160px', backgroundColor: '#F3F4F6', flexShrink: 0 }}>
                          {imageUrl
                            ? <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>🏗️</div>
                          }
                          {/* Edit + Delete icon buttons (don't open the project when clicked) */}
                          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => handleEditProject(item)}
                              style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              title="Edit"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => { setSelectedProject(item); setDeleteModalVisible(true); }}
                              style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              title="Delete"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            </button>
                            {item.approval_status !== 'approved' && (
                              <button
                                onClick={() => { setSelectedProject(item); setApprovalModalVisible(true); }}
                                style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Approve / Send to Task"
                              >✅</button>
                            )}
                          </div>
                          {/* Approval badge */}
                          <span style={{
                            position: 'absolute', bottom: 8, left: 8,
                            backgroundColor: approvalC.bg, color: approvalC.text,
                            padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
                            textTransform: 'capitalize'
                          }}>{item.approval_status || 'pending'}</span>
                        </div>

                        {/* Card body */}
                        <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.property_title || 'Untitled'}
                          </div>
                          {item.project_code && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>ID: {item.project_code}</div>
                          )}
                          {item.city && (
                            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '2px' }}>
                              📍 {item.city}{item.locality ? `, ${item.locality}` : ''}
                            </div>
                          )}
                          {item.property_type && (
                            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>🏠 {item.property_type}</div>
                          )}

                          {/* BHK + sqft tags */}
                          <div className="d-flex gap-1 flex-wrap mb-2">
                            {item.bedrooms && <span style={{ fontSize: '10px', fontWeight: '500', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 7px', borderRadius: '4px' }}>{item.bedrooms} BHK</span>}
                            {item.area_sqft && <span style={{ fontSize: '10px', fontWeight: '500', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 7px', borderRadius: '4px' }}>{item.area_sqft} sqft</span>}
                          </div>

                          {/* Footer: price + status + views */}
                          <div style={{ marginTop: 'auto' }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <span style={{ fontSize: '14px', fontWeight: '700', color: '#2563EB' }}>
                                {formatCurrency(item.price_min || item.price, item.price_max)}
                              </span>
                              <span style={{ backgroundColor: statusC.bg, color: statusC.text, padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600', textTransform: 'capitalize' }}>{item.status}</span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <span style={{ fontSize: '11px', color: '#9CA3AF' }}>👁 {item.views_count || 0} views</span>
                              <span style={{ fontSize: '11px', color: '#D1D5DB' }}>
                                {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : ''}
                              </span>
                            </div>
                          </div>

                          {/* Rejection reason */}
                          {item.approval_status === 'rejected' && item.rejection_reason && (
                            <div className="d-flex gap-2 mt-2 p-2" style={{ backgroundColor: '#FEF2F2', borderRadius: '8px' }}>
                              <span>⚠️</span>
                              <span style={{ fontSize: '11px', color: '#DC2626' }}>{item.rejection_reason}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── FAB: Add Project ── */}
      <button
        onClick={() => navigate('/AdminCrmAddProject', { state: { userId, userName, userRole, userEmail, userPhone, mode: 'add' } })}
        style={{
          position: 'fixed', bottom: '28px', right: '28px',
          width: '56px', height: '56px', borderRadius: '50%',
          backgroundColor: '#2563EB', border: 'none', color: '#fff',
          fontSize: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(37,99,235,0.4)', zIndex: 1000
        }}
        title="Add Project"
      >+</button>

      {/* ── Action Menu Modal ── */}
      {menuVisible && (
        <div
          onClick={() => setMenuVisible(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '16px 20px 34px', width: '100%', maxWidth: '560px' }}
          >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-1" style={{ paddingBottom: '12px', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827', flex: 1, marginRight: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedProject?.property_title || 'Project'}
              </span>
              <button onClick={() => setMenuVisible(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#6B7280', cursor: 'pointer' }}>✕</button>
            </div>

            {/* Edit */}
            <div
              onClick={() => handleEditProject()}
              className="d-flex align-items-center gap-3"
              style={{ padding: '14px 0', borderBottom: '1px solid #F9FAFB', cursor: 'pointer' }}
            >
              <span style={{ fontSize: '20px' }}>✏️</span>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#374151' }}>Edit Project</span>
            </div>

            {/* Approve / Send to Task */}
            {selectedProject?.approval_status !== 'approved' && (
              <div
                onClick={handleApproveFromMenu}
                className="d-flex align-items-center gap-3"
                style={{ padding: '14px 0', borderBottom: '1px solid #F9FAFB', cursor: 'pointer' }}
              >
                <span style={{ fontSize: '20px' }}>✅</span>
                <span style={{ fontSize: '15px', fontWeight: '500', color: '#10B981' }}>Approve / Send to Task</span>
              </div>
            )}

            {/* Delete */}
            <div
              onClick={handleDeleteProject}
              className="d-flex align-items-center gap-3"
              style={{ padding: '14px 0', cursor: 'pointer' }}
            >
              <span style={{ fontSize: '20px' }}>🗑️</span>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#EF4444' }}>Delete Project</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteModalVisible && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 24px 34px', width: '100%', maxWidth: '560px', textAlign: 'center' }}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Delete Project?</div>
            <div style={{ fontSize: '14px', color: '#6B7280', lineHeight: '20px', marginBottom: '20px' }}>
              Are you sure you want to delete "{selectedProject?.property_title}"? This action cannot be undone.
            </div>

            {actionLoading ? (
              <div style={{ padding: '16px', color: '#EF4444' }}>Deleting...</div>
            ) : (
              <div className="d-flex gap-3">
                <button
                  onClick={() => { setDeleteModalVisible(false); setSelectedProject(null); }}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: 'none', backgroundColor: '#F3F4F6', color: '#6B7280', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}
                >Cancel</button>
                <button
                  onClick={confirmDeleteProject}
                  style={{ flex: 1, padding: '14px', borderRadius: '12px', border: 'none', backgroundColor: '#EF4444', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}
                >Delete</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Approve / Send to Task Modal ── */}
      {approvalModalVisible && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 24px 34px', width: '100%', maxWidth: '560px', textAlign: 'center' }}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🛡️</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Project Verification</div>
            <div style={{ fontSize: '14px', color: '#6B7280', lineHeight: '20px', marginBottom: '20px' }}>
              Would you like to approve "{selectedProject?.property_title}" now or send it for verification?
            </div>

            {approvalLoading ? (
              <div style={{ padding: '20px', color: '#2563EB' }}>Processing...</div>
            ) : (
              <>
                <button
                  onClick={handleApproveNow}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', backgroundColor: '#10B981', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  ✔ Approve Now
                </button>
                <button
                  onClick={handleSendToTask}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #BFDBFE', backgroundColor: '#EFF6FF', color: '#2563EB', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  🕐 Send to Verification Task
                </button>
                <button
                  onClick={() => { setApprovalModalVisible(false); setSelectedProject(null); }}
                  style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: '#9CA3AF', fontSize: '14px', cursor: 'pointer' }}
                >Cancel</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
